import { invalidateConnectionModel } from './connectionModelResource';

export const MODEL_CHANGED_EVENT = 'unity:model-changed';
export const DATA_DEFINITIONS_REFRESH_EVENT = 'unity:data-definitions-refresh';

type ModelChange = { connectionId?: number; reportId?: string; dataChanged?: boolean; revision?: number };
/** `revision` is set only for another session's change: consumers whose reads already carry it skip. */
export type ModelChangeDetail = { connectionId: number; reportId?: string; dataChanged: boolean; revision?: number };

/** Whether a model change can change query results; a diagram layout save cannot. */
export const modelChangeAffectsData = (event: Event): boolean =>
    (event as CustomEvent<ModelChangeDetail>).detail?.dataChanged !== false;

/** Whether state read at `loadedRevision` already reflects another session's model change. */
export const modelChangeAlreadyLoaded = (event: Event, loadedRevision: unknown): boolean => {
    const revision = (event as CustomEvent<ModelChangeDetail>).detail?.revision;
    return revision != null && loadedRevision != null && Number(loadedRevision) >= revision;
};

const deliverModelChange = (change: ModelChange): void => {
    const connectionId = Number(change?.connectionId);
    if (!change?.reportId) return;
    invalidateConnectionModel(Number.isSafeInteger(connectionId) ? connectionId : 0, change.reportId);
    window.dispatchEvent(new CustomEvent(DATA_DEFINITIONS_REFRESH_EVENT, {
        detail: { connectionId, reportId: change.reportId, reason: 'model_changed' },
    }));
    window.dispatchEvent(new CustomEvent<ModelChangeDetail>(MODEL_CHANGED_EVENT, {
        detail: {
            connectionId, reportId: change.reportId, dataChanged: change.dataChanged !== false,
            ...(change.revision != null ? { revision: change.revision } : {}),
        },
    }));
};

/** Another session saved model `revision` (seen by report liveness). Local delivery only. */
export const deliverRemoteModelChange = (reportId: string, revision: number): void =>
    deliverModelChange({ reportId, revision, dataChanged: true });

// A report may be open in several tabs. Remote delivery is local
// invalidation only, so receiving a change never broadcasts it back.
const channel = typeof window.BroadcastChannel === 'function'
    ? new window.BroadcastChannel(MODEL_CHANGED_EVENT)
    : null;
if (channel) channel.onmessage = event => deliverModelChange(event.data);

export const publishModelChange = (connectionId: number | undefined, reportId?: string,
    options?: { dataChanged?: boolean }): void => {
    const change = { connectionId, reportId, dataChanged: options?.dataChanged !== false };
    deliverModelChange(change);
    channel?.postMessage(change);
};
